import { useState } from 'react';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import styled from 'styled-components';
import { api, BracketStage, MatchSummary, Match } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { Flag } from '../components/TeamBadge';
import { ScorersList } from '../components/ScorersList';
import { IconChevron } from '../components/NavIcons';
import { teamName } from '../lib/flags';
import { H2, Muted } from '../components/ui';

const Podium = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr 1fr;
  gap: 8px;
  align-items: end;
  margin: 8px 0 18px;
`;
const Step = styled.div<{ $h: number; $medal: string }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ $medal }) => $medal};
  border-radius: 12px 12px 0 0;
  padding: 12px 6px;
  min-height: ${({ $h }) => $h}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  text-align: center;
  .pos { font-size: 1.04rem; font-weight: 800; }
  .nm { font-weight: 700; font-size: 0.86rem; line-height: 1.1; }
`;

const Pending = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.accent}, ${({ theme }) => theme.colors.accentDark});
  color: #fff;
  border-radius: ${({ theme }) => theme.radius};
  padding: 18px;
  text-align: center;
  margin-bottom: 16px;
  .big { font-size: 1.2rem; font-weight: 800; margin-bottom: 4px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;
const Kpi = styled.div<{ $clickable?: boolean; $on?: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme, $on }) => ($on ? theme.colors.accentBorder : theme.colors.borderStrong)};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  .n { font-size: 1.7rem; font-weight: 800; color: ${({ theme }) => theme.colors.accentInk}; }
  .l { font-size: 0.79rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .hint { font-size: 0.63rem; font-weight: 700; color: ${({ theme }) => theme.colors.accentInk}; margin-top: 3px; }
`;

const DetailRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 9px 12px;
  margin-bottom: 6px;
  text-align: left;
  font-size: 0.88rem;
  .who { flex: 1; font-weight: 600; }
  .vs { color: ${({ theme }) => theme.colors.textMuted}; font-size: 0.79rem; }
`;

type DetailKey = 'pen' | 'og' | 'draws';

function DetailPanel({ which, matches, nav }: { which: DetailKey; matches: Match[]; nav: NavigateFunction }) {
  if (which === 'draws') {
    const list = matches.filter((m) => m.status === 'finished' && m.score && m.score.ft[0] === m.score.ft[1]);
    return (
      <>
        {list.length === 0 && <Muted>Todavía no hubo empates.</Muted>}
        {list.map((m) => (
          <DetailRow key={m.id} onClick={() => nav(`/match/${m.id}`)}>
            <Flag team={m.team1.resolved ?? m.team1.name} size={18} />
            <span className="who">
              {teamName(m.team1.resolved ?? m.team1.name)} {m.score!.ft[0]}–{m.score!.ft[1]}{' '}
              {teamName(m.team2.resolved ?? m.team2.name)}
            </span>
            <Flag team={m.team2.resolved ?? m.team2.name} size={18} />
          </DetailRow>
        ))}
      </>
    );
  }
  // pen / og → eventos de gol con ese flag
  const items: { id: number; player: string; team: string; vs: string }[] = [];
  for (const m of matches) {
    const t1 = m.team1.resolved ?? m.team1.name;
    const t2 = m.team2.resolved ?? m.team2.name;
    for (const g of m.goals1) if ((which === 'pen' ? g.penalty && !g.owngoal : g.owngoal)) items.push({ id: m.id, player: g.name, team: t1, vs: t2 });
    for (const g of m.goals2) if ((which === 'pen' ? g.penalty && !g.owngoal : g.owngoal)) items.push({ id: m.id, player: g.name, team: t2, vs: t1 });
  }
  return (
    <>
      {which === 'pen' && (
        <Muted style={{ marginBottom: 8, fontSize: '0.82rem' }}>
          Solo penales convertidos en gol — la fuente gratuita no registra los errados o atajados.
        </Muted>
      )}
      {items.length === 0 && <Muted>Sin datos todavía.</Muted>}
      {items.map((it, i) => (
        <DetailRow key={i} onClick={() => nav(`/match/${it.id}`)}>
          <Flag team={it.team} size={18} />
          <span className="who">{it.player}</span>
          <span className="vs">{teamName(it.team)} vs {teamName(it.vs)}</span>
        </DetailRow>
      ))}
    </>
  );
}

// Card clickeable: cursor + chevron a la derecha + borde de acento.
const ClickCard = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme }) => theme.colors.accentBorder};
  border-radius: ${({ theme }) => theme.radius};
  padding: 12px 14px;
  margin-bottom: 8px;
  text-align: left;
  .sc { font-weight: 800; font-size: 1.08rem; }
  .nm { flex: 1; font-weight: 600; }
  .chev { color: ${({ theme }) => theme.colors.textMuted}; display: inline-flex; }
`;

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 120px;
  margin-top: 8px;
  .bar { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; }
  .fill { width: 100%; background: ${({ theme }) => theme.colors.accent}; border-radius: 4px 4px 0 0; min-height: 2px; }
  .v { font-size: 0.68rem; font-weight: 700; margin-bottom: 2px; }
  .k { font-size: 0.59rem; color: ${({ theme }) => theme.colors.textMuted}; margin-top: 4px; }
`;

const SeeAll = styled.button`
  width: 100%;
  text-align: center;
  padding: 10px;
  margin: 4px 0 8px;
  font-weight: 700;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.accentInk};
`;

const teamOf = (t: { resolved: string | null; name: string }) => t.resolved ?? t.name;

function podium(stages: BracketStage[]): { champ: string; runner: string; third: string | null } | null {
  const all = stages.flatMap((s) => s.matches);
  const final = all.find((m) => m.stage?.key === 'final');
  const third = all.find((m) => m.stage?.key === 'third');
  if (!final || !final.score) return null;
  const [a, b] = final.score.ft;
  if (a === b) return null;
  const champ = a > b ? teamOf(final.team1) : teamOf(final.team2);
  const runner = a > b ? teamOf(final.team2) : teamOf(final.team1);
  let thirdTeam: string | null = null;
  if (third && third.score && third.score.ft[0] !== third.score.ft[1]) {
    thirdTeam = third.score.ft[0] > third.score.ft[1] ? teamOf(third.team1) : teamOf(third.team2);
  }
  return { champ, runner, third: thirdTeam };
}

function MatchLink({ m }: { m: MatchSummary }) {
  const navigate = useNavigate();
  return (
    <ClickCard onClick={() => navigate(`/match/${m.id}`)}>
      <Flag team={m.team1} size={20} />
      <span className="nm">
        {teamName(m.team1)} <span className="sc">{m.ft[0]}–{m.ft[1]}</span> {teamName(m.team2)}
      </span>
      <Flag team={m.team2} size={20} />
      <span className="chev"><IconChevron size={18} /></span>
    </ClickCard>
  );
}

export function Resumen() {
  const navigate = useNavigate();
  const stats = useAsync(() => api.stats(), [], '/stats');
  const scorers = useAsync(() => api.scorers(), [], '/scorers');
  const bracket = useAsync(() => api.bracket(), [], '/bracket');
  const fixtures = useAsync(() => api.fixtures(), [], '/fixtures');
  const [detail, setDetail] = useState<DetailKey | null>(null);
  const toggle = (k: DetailKey) => setDetail((d) => (d === k ? null : k));

  return (
    <>
      <h2 style={{ marginTop: 4 }}>Resumen del torneo</h2>

      <Async state={bracket}>
        {(d) => {
          const p = podium(d.stages);
          if (p) {
            return (
              <Podium>
                <Step $h={120} $medal="#c0c0c0">
                  <span className="pos">2º</span>
                  <Flag team={p.runner} size={26} />
                  <span className="nm">{teamName(p.runner)}</span>
                </Step>
                <Step $h={150} $medal="#f4b324">
                  <span className="pos">1º</span>
                  <Flag team={p.champ} size={34} />
                  <span className="nm">{teamName(p.champ)}</span>
                </Step>
                <Step $h={100} $medal="#cd7f32">
                  <span className="pos">3º</span>
                  {p.third && <Flag team={p.third} size={24} />}
                  <span className="nm">{p.third ? teamName(p.third) : '—'}</span>
                </Step>
              </Podium>
            );
          }
          const fin = d.stages.flatMap((s) => s.matches).find((m) => m.stage?.key === 'final');
          return (
            <Pending>
              <div className="big">El campeón se define en la final</div>
              {fin && <div>{teamName(teamOf(fin.team1))} vs {teamName(teamOf(fin.team2))}</div>}
            </Pending>
          );
        }}
      </Async>

      <Async state={stats}>
        {({ stats: s }) => {
          const maxMin = Math.max(1, ...Object.values(s.goalsByMinute));
          return (
            <>
              <Grid>
                <Kpi><div className="n">{s.goals}</div><div className="l">goles</div></Kpi>
                <Kpi><div className="n">{s.avg}</div><div className="l">por partido</div></Kpi>
                <Kpi><div className="n">{s.played}</div><div className="l">partidos jugados</div></Kpi>
                <Kpi $clickable $on={detail === 'pen'} onClick={() => toggle('pen')}>
                  <div className="n">{s.penalties}</div><div className="l">goles de penal</div>
                  <div className="hint">{detail === 'pen' ? 'ocultar ▲' : 'ver ▼'}</div>
                </Kpi>
                <Kpi $clickable $on={detail === 'og'} onClick={() => toggle('og')}>
                  <div className="n">{s.ownGoals}</div><div className="l">en contra</div>
                  <div className="hint">{detail === 'og' ? 'ocultar ▲' : 'ver ▼'}</div>
                </Kpi>
                <Kpi $clickable $on={detail === 'draws'} onClick={() => toggle('draws')}>
                  <div className="n">{s.draws}</div><div className="l">empates</div>
                  <div className="hint">{detail === 'draws' ? 'ocultar ▲' : 'ver ▼'}</div>
                </Kpi>
              </Grid>

              {detail && (
                <div style={{ marginTop: 10 }}>
                  <DetailPanel which={detail} matches={fixtures.data?.matches ?? []} nav={navigate} />
                </div>
              )}

              {s.biggestWin && (
                <>
                  <H2>Mayor goleada</H2>
                  <MatchLink m={s.biggestWin} />
                </>
              )}
              {s.mostGoals && s.mostGoals.id !== s.biggestWin?.id && (
                <>
                  <H2>Partido con más goles ({s.mostGoals.total})</H2>
                  <MatchLink m={s.mostGoals} />
                </>
              )}

              {s.hatTricks.length > 0 && (
                <>
                  <H2>Hat-tricks</H2>
                  {s.hatTricks.map((h, i) => (
                    <ClickCard key={i} onClick={() => navigate(`/match/${h.matchId}`)}>
                      <Flag team={h.team} size={20} />
                      <span className="nm">{h.player} — {h.goals} goles</span>
                      <span className="chev"><IconChevron size={18} /></span>
                    </ClickCard>
                  ))}
                </>
              )}

              <H2>¿Cuándo se hacen los goles?</H2>
              <Bars>
                {Object.entries(s.goalsByMinute).map(([k, v]) => (
                  <div className="bar" key={k}>
                    <span className="v">{v}</span>
                    <div className="fill" style={{ height: `${(v / maxMin) * 100}%` }} />
                    <span className="k">{k}</span>
                  </div>
                ))}
              </Bars>
              <Muted style={{ marginTop: 8 }}>Minuto del partido (incluye descuento).</Muted>
            </>
          );
        }}
      </Async>

      <H2>Goleadores</H2>
      <Muted style={{ marginBottom: 8, fontSize: '0.85rem' }}>Tocá un goleador para ver su selección.</Muted>
      <Async state={scorers}>
        {(d) => (
          <>
            <ScorersList scorers={d.scorers.slice(0, 10)} />
            {d.scorers.length > 10 && <SeeAll onClick={() => navigate('/scorers')}>Ver ranking completo →</SeeAll>}
          </>
        )}
      </Async>

      <Muted style={{ textAlign: 'center', margin: '14px 4px 4px', fontSize: '0.92rem' }}>
        Versión gratuita: los datos se actualizan ~2 horas después de cada partido.
      </Muted>
    </>
  );
}
